<template>
  <div>
    <employee-probation-reminder
      v-if="isCurrentUser && $hasPermissionTo(['view probations']) && probations.length>0"
      :probation="probations"
      @load="loadProbations"
    />
    <v-container fluid>
      <v-row>
        <v-col class="text-center pt-4">
          <v-skeleton-loader
            v-if="skeleton"
            class="mx-auto custom-loading"
            :loading="skeleton"
            type="avatar"
          />  
          <div v-else>
            <helper-image-input
              v-model="avatar"
              :max-size-in-mb="10"
              field-name="avatar"
              accept-type="image/*" 
            >
              <template v-slot:activator="{ on }">
                <img
                  v-if="!avatar"
                  slot="activator"
                  :src="thumbnailUrl"
                  onerror="this.src='img/thumbnail.jpg'"
                  class="avatar-size"
                  :class="{
                    'cursor-pointer': canUploadProfilePicture(),
                  }"
                  v-on="on"
                  @click="restrictUploadProfilePicture"
                >
              </template>
              <img
                v-if="!avatar"
                slot="activator"
                :src="thumbnailUrl"
                onerror="this.src='img/thumbnail.jpg'"
                class="avatar-size cursor-pointer"
              >
            </helper-image-input>
            <img
              v-if="(avatar||{}).imageURL"
              :src="avatar.imageURL"
              class="avatar-size"
            >
          </div>

          <div 
            v-if="avatar"
            class="text-center"
          >         
            <v-btn 
              :disabled="saving"
              text
              icon
              @click="avatar = null"
            >
              <v-icon size="30">
                mdi-close-circle-outline
              </v-icon>
            </v-btn>   
            <v-btn 
              :loading="saving"
              text
              icon
              color="primary"
              @click="uploadImage" 
            >
              <v-icon size="30">
                mdi-cloud-upload
              </v-icon>
            </v-btn>
          </div>
        </v-col>

        <v-col class="text-left my-auto">
          <v-skeleton-loader
            v-if="skeleton"
            type="list-item-three-line"
          />  

          <span v-else>
            <div class="font-calibri font-18 line-height-1 font-grey">
              {{ (employeeNumberStarter||{}).value }}{{ (employee||{}).employee_number }} 
            </div>
            <div class="font-weight-bold font-calibri font-18 line-height-1">
              {{ ((employee||{}).person||{}).name }}
            </div>
            <div class="font-calibri font-18 font-italic line-height-1">
              {{ ((employee||{}).department||{}).name }}
            </div>
            <div class="font-calibri font-14 font-italic line-height-1 font-grey">
              {{ (((employee||{}).current_position||{}).position||{}).name }}
            </div>
          </span>
        </v-col>
       
        <v-col class="text-left my-auto">
          <v-skeleton-loader
            v-if="skeleton"
            type="list-item-two-line"
          />  

          <span v-else>
            <div class="font-calibri font-grey font-italic">
              <v-icon class="pr-2">
                mdi-email
              </v-icon>
              {{ employee.email ? employee.email : 'N/A' }}
            </div>

            <div class="font-calibri font-grey font-italic">
              <v-icon class="pr-2">
                mdi-phone
              </v-icon>
              {{ ((employee||{}).person||{}).handphone_number? employee.person.handphone_number : 'N/A' }}
            </div>
          </span>
        </v-col>

        <v-col class="text-left my-auto">
          <v-skeleton-loader
            v-if="skeleton"
            type="list-item-two-line"
          />  

          <span v-else>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="font-calibri font-grey font-italic"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon class="pr-2">
                    mdi-calendar
                  </v-icon>
                  {{ employee.joined_date ? $moment(employee.joined_date).format('DD MMM YYYY') : 'N/A' }}
                </div>
              </template>
              <span>Join date</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="font-calibri font-grey font-italic"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon class="pr-2">
                    mdi-calendar
                  </v-icon>
                  {{ yearOfServiceDisplay }} year(s)
                </div>
              </template>
              <span>Year of service</span>
            </v-tooltip>
          </span>
        </v-col>

        <v-col
          class="text-left my-auto"
          cols="1"
        >
          <v-skeleton-loader
            v-if="skeleton"
            type="list-item"
          />  
          <span v-else>
            <div v-if="employee.is_active_employment">
              <v-fade-transition>
                <v-avatar
                  :style="{
                    animationDuration: `0.9s`
                  }"
                  :color="getStatusColor(employee)"
                  class="mb-1 v-avatar--metronome"
                  size="12"
                />
              </v-fade-transition>
              <span 
                class="ml-1 green--text font-calibri"
              > Active </span>
            </div>

            <div v-else>
              <v-fade-transition>
                <v-avatar
                  :style="{
                    animationDuration: `5s`
                  }"
                  :color="getStatusColor(employee)"
                  class="mb-1 v-avatar--metronome"
                  size="12"
                />
              </v-fade-transition>         
              <span 
                v-if="isResigned(employee)" 
                class="ml-1 grey--text font-calibri"
              > 
                {{ 
                  employee.current_separation ? employee.current_separation.type.name : 'Resigned' }} 
              </span>
              <span 
                v-else-if="isPending(employee)" 
                class="ml-1 warning--text font-calibri"
              > Pending </span>
              <span 
                v-else 
                class="ml-1 info--text font-calibri"
              > On Leave </span>
            </div> 
          </span>
        </v-col>
        <v-col />
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  export default {
    components: {
      HelperImageInput: () => import('@/components/hris/helper/ImageInput'),
      EmployeeProbationReminder: () => import('@/components/hris/employee/ProbationReminder'),

    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        employee: {},
        avatar: null,
        defaultAvatar: 'img/thumbnail.jpg',
        skeleton: false,
        saving: false,
        employeeNumberLengthSetting: [],
        employeeNumberStarter: [],
        probations: [],
      }
    },
    computed: {
      ...mapState({ isCurrentUser: 'isMyProfile' }),
      images () {
        return [ this.avatarUrl ]
      },
      avatarUrl () {
        return this.employee.avatar_url
      },
      thumbnailUrl () {
        return this.employee.thumbnail_url || this.defaultAvatar
      },
      yearOfServiceDisplay () {
        var noOfDaysInCurrentYear = this.$moment().endOf('year').diff(this.$moment().startOf('year'), 'days') + 1 // includes day 1
        if (this.employee.end_date) {
          return ((this.$moment(this.employee.end_date).diff(this.$moment(this.employee.joined_date), 'days') + 1) / noOfDaysInCurrentYear).toFixed(2)
        }
        return ((this.$moment().diff(this.$moment(this.employee.joined_date), 'days') + 1) / noOfDaysInCurrentYear).toFixed(2)
      },
      isMyProfile () {
        return this.$store.state.isMyProfile
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.$resetData()
            this.loadSystemSettings()
          }
        },
      },
    },
    methods: {    
      canUploadProfilePicture () {
        return this.$hasPermissionTo(['upload profile pictures']) && !this.isMyProfile
      },
      restrictUploadProfilePicture (event) {
        if (!this.canUploadProfilePicture()) {
          event.stopPropagation() 
        }
      },
      uploadImage () {
        this.saving = true

        this.$api.avatars().upload(this.employeeUid, this.avatar.formData)
          .then(res => {
            if (res.status >= 200 && res.status < 300) {   
              this.loadEmployee()
              this.avatar = null
              this.saving = false
              this.$snack({ message: res.data.status })
            }          
          })
          .catch(() => {
            this.saving = false
            this.$snack({
              color: 'error',
              message: 'Photo upload failed, please try again',
            })
          }) 
      },

      loadSystemSettings () {
        this.$api.systemSettings().list()
          .then((res) => {
            this.employeeNumberLengthSetting = res.data.find(setting => 
              setting.key === 'EMPLOYEE_NUMBER_LENGTH'
            )
            this.employeeNumberStarter = res.data.find(setting => 
              setting.key === 'EMPLOYEE_NUMBER_STARTER'
            )
            this.loadEmployee()
          })
          .catch(() => {})
      },

      loadEmployee () {
        this.skeleton = true

        this.$api.hrisEmployees().show(this.employeeUid)
          .then(response => {
            this.employee = response.data

            if (this.employee && this.employee.employee_number) {
              // preset 10 digits of 0
              this.employee.employee_number = '0000000000' + this.employee.employee_number 
              // cut the digits based on EMPLOYEE_NUMBER_LENGTH setting
              this.employee.employee_number = this.employee.employee_number.substring(this.employee.employee_number.length - this.employeeNumberLengthSetting.value, this.employee.employee_number.length)
            }

            this.skeleton = false

            if (this.$hasPermissionTo(['view probations'])) {
              this.loadProbations()
            }
          })
      },
      loadProbations () {
        this.$api.employeeProbations().list()
          .then(res => {
            var maxEndDate = this.$moment().add(1, 'M').format('YYYY-MM-DD')
            this.probations = res.data.filter(i => {
              return i.end_date <= maxEndDate 
            })
            this.probations = _.orderBy(this.probations, ['start_date'], ['desc'])
          })
      },
      getStatusColor (employee) {
        if (this.isResigned(employee)) {
          return 'grey'
        } else if (this.isPending(employee)) {
          return 'warning'
        } else if (this.isOnLeave(employee)) {
          return 'info'
        } else {
          return 'green'
        }
      },
      isResigned (employee) {
        return employee.end_date !== null && employee.end_date <= this.$moment().format('YYYY-MM-DD')
      },
      isPending (employee) {
        return employee.joined_date > this.$moment().format('YYYY-MM-DD')
      },
      isOnLeave (employee) {
        return employee.is_active === 0
      },
    },
  }
</script>
<style scoped>
.font-calibri{
  font-family: calibri;
}

.font-14{
  font-size: 14px;
}

.font-18{
  font-size: 18px;
}

.font-italic{
  font-style: italic;
}

.line-height-1{
  line-height: 1.0;
}

.font-grey{
  color: #95989A;
}

.v-avatar--metronome {
  animation-name: metronome-example;
  animation-iteration-count: infinite;
  animation-direction: alternate;
}

@keyframes metronome-example {
  from {
    transform: scale(.5);
  }

  to {
    transform: scale(1);
  }
}

.custom-loading >>> .v-skeleton-loader__avatar.v-skeleton-loader__bone{
  margin:auto;
  min-width: 130px;
  min-height: 130px;
}

.avatar-size{
  border-radius:50% !important;
  height:130px;
  width:130px;
}

.cursor-pointer
{
  cursor:pointer !important;
}
</style>
